import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { CreateEditStatisticModalComponent } from '@connect-services/client/components/modals/create-edit-statistic-modal/create-edit-statistic-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { SecurityService } from '@connect-services/client/services/security.service';
import { StatisticService } from '@connect-services/client/services/statistic.service';
import { ComputedStatistic } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

type ConfirmModalData = {
    cancelLabel?: string;
    closeAction?: boolean;
    confirmLabel?: string;
    description?: string;
    title: string;
    type: ModalType;
};

type ConfirmModalConfig = {
    couldDelete: boolean;
    modalData: ConfirmModalData;
};

type TrashIconConfig = {
    className: string;
    disabled: boolean;
    onClick: () => void;
    tooltip?: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-statistic-edit-order',
    templateUrl: './statistic-edit-order.component.html',
})
export class StatisticEditOrderComponent {
    private static readonly baseTrashConfig = {
        className: 'cursor-pointer hover:text-red-500',
        disabled: false,
    };

    public readonly computedStatistic: InputSignal<ComputedStatistic> = input.required<ComputedStatistic>();

    protected readonly isOwnerAndShared = computed(() => {
        return (
            this.computedStatistic().createdBy?.id === this.securityService.meSignal()!.id &&
            this.computedStatistic().isShared
        );
    });
    protected readonly isPrivateStatistic = computed(() => {
        return (
            this.computedStatistic().createdBy?.id === this.securityService.meSignal()!.id &&
            !this.computedStatistic().isShared
        );
    });
    protected readonly securityService = inject(SecurityService);
    protected readonly trashIconConfig = computed<TrashIconConfig>(() => {
        if (this.isCommonStatistic()) {
            return {
                ...StatisticEditOrderComponent.baseTrashConfig,
                onClick: (): void => {
                    void this.hideCommonStatistic();
                },
                tooltip: this.translocoService.translate('statistic.deleteStatistic'),
            };
        }
        if (this.isOwnerAndShared()) {
            return {
                ...StatisticEditOrderComponent.baseTrashConfig,
                onClick: (): void => {
                    void this.deleteOrHideSharedStatistic();
                },
                tooltip: this.translocoService.translate('statistic.deleteStatistic'),
            };
        }
        if (this.isPrivateStatistic()) {
            return {
                ...StatisticEditOrderComponent.baseTrashConfig,
                onClick: (): void => {
                    void this.deleteOrHideUnsharedStatistic();
                },
                tooltip: this.translocoService.translate('statistic.deleteStatistic'),
            };
        }

        return {
            className: 'opacity-40 cursor-not-allowed',
            disabled: true,
            onClick: (): void => {},
            tooltip: this.translocoService.translate('statistic.cannotDeleteIfNotOwner'),
        };
    });
    private readonly isCommonStatistic = computed(() => {
        return !this.computedStatistic().organization && !this.computedStatistic().createdBy;
    });

    private readonly matDialog = inject(MatDialog);
    private readonly statisticService = inject(StatisticService);
    private readonly translocoService: TranslocoService = inject(TranslocoService);

    protected openEditStatisticDialog(): void {
        const dialogRef = this.matDialog.open(CreateEditStatisticModalComponent, {
            autoFocus: true,
            data: {
                isEditMode: true,
                payloadStatistic: this.computedStatistic(),
                title: 'statistic.editStatistic',
            },
            width: '40rem',
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (!result) {
                return;
            }
            await this.statisticService.setStatistics();
        });
    }

    private async deleteOrHideSharedStatistic(): Promise<void> {
        await this.handleConfirmationResult({
            couldDelete: true,
            modalData: {
                cancelLabel: this.translocoService.translate('deleteForAll'),
                closeAction: true,
                confirmLabel: this.translocoService.translate('meOnly'),
                description: this.translocoService.translate('statistic.deleteScopeQuestion'),
                title: this.translocoService.translate('statistic.sharedWithOtherUsers'),
                type: ModalType.ERROR,
            },
        });
    }

    private async deleteOrHideUnsharedStatistic(): Promise<void> {
        await this.handleConfirmationResult({
            couldDelete: true,
            modalData: {
                cancelLabel: this.translocoService.translate('definitely'),
                closeAction: true,
                confirmLabel: this.translocoService.translate('statistic.fromDashboard'),
                description: this.translocoService.translate('statistic.dashboardOnlyReappearOnReset'),
                title: this.translocoService.translate('statistic.deleteStatisticTitle'),
                type: ModalType.ERROR,
            },
        });
    }

    private async handleConfirmationResult(confirmModalConfig: ConfirmModalConfig): Promise<void> {
        const result: ModalResultEnum | undefined = await this.openConfirm(confirmModalConfig.modalData);

        if (result === ModalResultEnum.CONFIRM) {
            await this.hide();
        }
        if (result !== ModalResultEnum.CANCEL || !confirmModalConfig.couldDelete) {
            return;
        }

        await this.statisticService.deleteStatistic(this.computedStatistic().id);
        await this.statisticService.setStatistics();
    }

    private async hide(): Promise<void> {
        await this.statisticService.hideStatisticForUser(
            this.securityService.meSignal()!.id,
            this.computedStatistic().id,
        );

        await this.statisticService.setStatistics(this.securityService.meSignal()!.id);
    }

    private async hideCommonStatistic(): Promise<void> {
        await this.handleConfirmationResult({
            couldDelete: false,
            modalData: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('confirm'),
                description: this.translocoService.translate('statistic.otherUsersWillStillSeeStatistic'),
                title: this.translocoService.translate('statistic.deleteStatisticFromDashboardOnly'),
                type: ModalType.ERROR,
            },
        });
    }

    private openConfirm(data: ConfirmModalData): Promise<ModalResultEnum | undefined> {
        const matDialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data,
            width: '30vw',
        });

        return firstValueFrom(matDialogRef.afterClosed());
    }
}
